import highHandsEn from "src/locales/en/highHands.json";
import highHandsFr from "src/locales/fr/highHands.json";
import { isPayloadSource } from "src/utils/hubSource";
import {
  fetchHighHandById,
  fetchHighHandBySlug,
} from "src/api/payload/highHand";
import {
  isMeaningfulHighHandPage,
  mapJsonHighHandToPage,
} from "src/utils/mapHighHand";

/** Hash labels for High Hand tabs (legacy HighHandMain + HighHandPageContent). */
export const HIGH_HAND_TAB_HASH = {
  en: {
    highHand: "highHand",
    anniversary: "anniversary",
  },
  fr: {
    highHand: "mainHaute",
    anniversary: "anniversaire",
  },
};

/**
 * Final path segment for `/[fr/]promotions/poker/:slug`.
 * @param {string} pathname
 * @returns {string}
 */
export const highHandSlugFromPathname = (pathname) => {
  if (typeof pathname !== "string" || !pathname.trim()) return "";
  const cleaned = pathname.replace(/\/+$/, "");
  const parts = cleaned.split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
};

/**
 * Match `/promotions/poker/:slug` or `/fr/promotions/poker/:slug`.
 * @param {string} pathname
 * @returns {{ slug: string, locale: 'en'|'fr' } | null}
 */
export const matchHighHandPromotionPath = (pathname) => {
  if (typeof pathname !== "string" || !pathname.trim()) return null;
  const cleaned = pathname.replace(/\/+$/, "");
  const match = cleaned.match(/^\/(?:(fr)\/)?promotions\/poker\/([^/]+)$/);
  if (!match) return null;
  return {
    locale: match[1] === "fr" ? "fr" : "en",
    slug: match[2],
  };
};

/**
 * @param {string} slug
 * @param {'en'|'fr'|string} locale
 * @returns {string}
 */
export const highHandPathForLocale = (slug, locale) => {
  const segment = typeof slug === "string" ? slug.trim() : "";
  if (!segment) return locale === "fr" ? "/fr/promotions/poker" : "/promotions/poker";
  return locale === "fr"
    ? `/fr/promotions/poker/${segment}`
    : `/promotions/poker/${segment}`;
};

/**
 * Resolve the translated URL for a Payload-backed High Hand page.
 * Uses the current localized slug to load the doc, then the other locale's slug.
 *
 * @param {string} pathname
 * @param {'en'|'fr'|string} nextLanguage
 * @returns {Promise<string|null>}
 */
export async function resolvePayloadHighHandLanguagePath(
  pathname,
  nextLanguage,
) {
  if (!isPayloadSource("high-hand")) return null;

  const matched = matchHighHandPromotionPath(pathname);
  if (!matched) return null;

  const next = nextLanguage === "fr" ? "fr" : "en";
  if (matched.locale === next) {
    return highHandPathForLocale(matched.slug, next);
  }

  try {
    const page = await fetchHighHandBySlug(matched.slug, matched.locale, {
      depth: 0,
    });
    if (page?.id == null) return null;

    const translated = await fetchHighHandById(page.id, next, { depth: 0 });
    if (!translated?.slug) return null;

    return highHandPathForLocale(translated.slug, next);
  } catch (error) {
    console.error(
      "Failed to resolve Payload high-hand language path",
      error,
    );
    return null;
  }
}

export const getHighHandData = async (location, i18n) => {
  let highHandData = null;

  try {
    const { pathname } = location;
    const highHandsConfig =
      i18n.language === "fr" ? highHandsFr.highHands : highHandsEn.highHands;

    for (const highHandKey in highHandsConfig) {
      const pages = highHandsConfig[highHandKey];
      if (pathname.includes(pages)) {
        const highHandDataLocale = await import(
          /* webpackChunkName: "high-hand-data" */ `src/locales/${i18n.language}/highHands/${highHandKey}.json`
        );
        highHandData = highHandDataLocale[highHandKey] || null;
        return { highHandData, highHandKey };
      }
    }

    throw new Error(
      `No matching high hand or tab found for pathname: ${pathname}`,
    );
  } catch (error) {
    console.error(
      `Error loading high hand data for pathname ${location.pathname}: ${error.message}`,
    );
    throw new Error(
      `No matching high hand or tab found for pathname: ${location.pathname}`,
    );
  }
};

/**
 * Resolve High Hand page props for runtime routes.
 * When `high-hand` is in REACT_APP_PAYLOAD_SOURCES, loads from Payload
 * (JSON fallback on error / empty or shell-only doc).
 *
 * @param {{ pathname: string }} location
 * @param {string} locale
 * @returns {Promise<object|null>}
 */
export async function resolveHighHandPage(location, locale) {
  const lang = locale === "fr" ? "fr" : "en";
  const slug = highHandSlugFromPathname(location?.pathname);

  if (isPayloadSource("high-hand") && slug) {
    try {
      const page = await fetchHighHandBySlug(slug, lang);
      if (isMeaningfulHighHandPage(page)) return page;
    } catch (error) {
      console.error(
        "Payload high-hand fetch failed; falling back to JSON locales",
        error,
      );
    }
  }

  try {
    const { highHandData, highHandKey } = await getHighHandData(location, {
      language: lang,
    });
    if (!highHandData || !highHandKey) return null;
    return mapJsonHighHandToPage(highHandData, highHandKey);
  } catch {
    return null;
  }
}
